// Observe → App versions (client compatibility design §5, monitor phase 1):
// status cards per platform plus pre-gate builds, and the stacked adoption
// chart. Read-only. Floors, the OK / prompted / blocked split and the
// template chips render only when the backend returns a policy snapshot;
// otherwise the cards show weekly active users by build. The change dialog,
// approvals, history and gates come with CV-S2.

import { useEffect, useState } from "react";
import {
  getAppVersionsAdoption,
  getAppVersionsOverview,
  type AppPlatform,
  type AppVersionsAdoption,
  type AppVersionsOverview,
  type BuildStatus,
  type BuildUsers,
  type PlatformOverview,
  type PolicyInfo,
} from "../services/appVersions";
import { formatDateTime } from "../components/analytics/format";
import { AdoptionChart, AdoptionLegend } from "../components/appVersions/AdoptionChart";
import { formatShare, shortDay } from "../components/appVersions/format";

const PLATFORM_LABEL: Record<AppPlatform, string> = { ios: "iOS", android: "Android" };
const PLATFORM_TAG: Record<AppPlatform, string> = { ios: "iOS", android: "AND" };
const STATUS_LABEL: Record<BuildStatus, string> = {
  ok: "OK",
  prompted: "Prompted",
  blocked: "Blocked",
  unknown: "No build",
};
const BUILD_ROWS = 5;

function errorMessage(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

function PolicyChips({ policy }: { policy: PolicyInfo | null }) {
  if (!policy) {
    return (
      <span className="chip" title="Floors appear once the backend reads the Remote Config policy.">
        Policy not connected
      </span>
    );
  }
  const published = [policy.publishedBy, policy.publishedAt && formatDateTime(policy.publishedAt)]
    .filter(Boolean)
    .join(" · ");
  return (
    <>
      <span className="chip" title={published || undefined}>
        Remote Config {policy.templateVersion ? `v${policy.templateVersion}` : "version unknown"}
      </span>
      {policy.inSync === true && <span className="chip av-chip-ok">Backend in sync</span>}
      {policy.inSync === false && (
        <span className="chip av-chip-warn">
          Backend on v{policy.backendTemplateVersion} · syncing
        </span>
      )}
      {published && <span className="note">Published {published}</span>}
    </>
  );
}

function BuildList({ builds, wau }: { builds: BuildUsers[]; wau: number }) {
  if (builds.length === 0) return <div className="muted av-small">No users on this platform this week.</div>;
  const shown = builds.slice(0, BUILD_ROWS);
  const rest = builds.slice(BUILD_ROWS).reduce((n, b) => n + b.users, 0);
  return (
    <table className="guide-table av-builds">
      <thead>
        <tr>
          <th className="no-sort">Build</th>
          <th className="no-sort">Users</th>
          <th className="no-sort">Share</th>
          {shown.some((b) => b.status) && <th className="no-sort">Status</th>}
        </tr>
      </thead>
      <tbody>
        {shown.map((b) => (
          <tr key={b.build ?? "unknown"}>
            <td className="mono">{b.build ?? "no build"}</td>
            <td className="av-num">{b.users.toLocaleString()}</td>
            <td className="av-num">{formatShare(b.users, wau)}</td>
            {b.status && (
              <td>
                <span className={`badge av-status-${b.status}`}>{STATUS_LABEL[b.status]}</span>
              </td>
            )}
          </tr>
        ))}
        {rest > 0 && (
          <tr>
            <td className="muted">{builds.length - BUILD_ROWS} more</td>
            <td className="av-num">{rest.toLocaleString()}</td>
            <td className="av-num">{formatShare(rest, wau)}</td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

function PlatformCard({ data }: { data: PlatformOverview }) {
  const { floors, split, wau } = data;
  return (
    <article className="panel av-card">
      <div className="av-card-head">
        <h2>
          <span className="av-plat">{PLATFORM_TAG[data.platform]}</span>
          {PLATFORM_LABEL[data.platform]}
        </h2>
        <span className="muted av-small">{wau.toLocaleString()} weekly active</span>
      </div>
      {floors && (
        <div className="av-trio">
          <div className="av-kv">
            <div className="kpi-label">Minimum</div>
            <div className="av-kv-value">{floors.minBuild}</div>
          </div>
          <div className="av-kv">
            <div className="kpi-label">Recommended</div>
            <div className="av-kv-value">{floors.recommendedBuild}</div>
          </div>
          <div className="av-kv">
            <div className="kpi-label">Live in store</div>
            <div className="av-kv-value">{floors.latestStoreBuild}</div>
          </div>
        </div>
      )}
      {split && wau > 0 && (
        <>
          <div className="av-bar" aria-hidden="true">
            <span className="ok" style={{ width: `${(split.ok / wau) * 100}%` }} />
            <span className="pr" style={{ width: `${(split.prompted / wau) * 100}%` }} />
            <span className="bl" style={{ width: `${(split.blocked / wau) * 100}%` }} />
          </div>
          <div className="av-legend">
            <span><i className="ok" />OK <b>{formatShare(split.ok, wau)}</b></span>
            <span><i className="pr" />Prompted <b>{formatShare(split.prompted, wau)}</b></span>
            <span><i className="bl" />Blocked <b>{formatShare(split.blocked, wau)}</b></span>
            {split.unknown > 0 && <span>No build <b>{formatShare(split.unknown, wau)}</b></span>}
          </div>
        </>
      )}
      <BuildList builds={data.builds} wau={wau} />
    </article>
  );
}

function LegacyCard({ overview }: { overview: AppVersionsOverview }) {
  const { legacy } = overview;
  return (
    <article className="panel av-card">
      <div className="av-card-head">
        <h2>
          <span className="av-plat">—</span>Pre-gate builds
        </h2>
        {legacy.blocked !== null && (
          <span className={`badge ${legacy.blocked ? "av-status-blocked" : "av-status-ok"}`}>
            {legacy.blocked ? "Blocked" : "Allowed"}
          </span>
        )}
      </div>
      <p className="av-legacy-line">
        Builds 1.2.x and older send no version headers. <b>{legacy.wau.toLocaleString()}</b> weekly
        active users, <b>{legacy.share === null ? "—" : formatShare(legacy.wau, overview.totalWau)}</b>{" "}
        of all. If blocked, they see the server's update text in error toasts; they can't show the
        full update screen.
      </p>
    </article>
  );
}

function AdoptionSection() {
  const [platform, setPlatform] = useState<AppPlatform>("ios");
  const [data, setData] = useState<AppVersionsAdoption | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    getAppVersionsAdoption(platform, 30)
      .then((d) => !cancelled && setData(d))
      .catch((e) => !cancelled && setError(errorMessage(e)));
    return () => {
      cancelled = true;
    };
  }, [platform]);

  const empty = data && data.points.every((p) => p.total === 0);
  return (
    <section className="panel av-section" aria-labelledby="av-adoption">
      <div className="av-card-head">
        <h2 id="av-adoption">Active users by build · last 30 days</h2>
        <div className="seg" role="group" aria-label="Platform">
          {(["ios", "android"] as AppPlatform[]).map((p) => (
            <button
              key={p}
              type="button"
              className={platform === p ? "active" : ""}
              aria-pressed={platform === p}
              onClick={() => setPlatform(p)}
            >
              {PLATFORM_LABEL[p]}
            </button>
          ))}
        </div>
      </div>
      {error ? (
        <div className="state-box error">Adoption unavailable ({error}).</div>
      ) : !data ? (
        <div className="state-box">Loading adoption…</div>
      ) : empty ? (
        <div className="state-box">No {PLATFORM_LABEL[platform]} users in the last 30 days.</div>
      ) : (
        <>
          <div className="av-chart">
            <AdoptionChart data={data} />
          </div>
          <AdoptionLegend data={data} />
          <p className="note">
            Each day counts the users seen in the 7 days ending that day (UTC), on their latest build.
            The last point is live; earlier ones come from the nightly rollup.
          </p>
        </>
      )}
    </section>
  );
}

export function AppVersionsPage() {
  const [overview, setOverview] = useState<AppVersionsOverview | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getAppVersionsOverview()
      .then(setOverview)
      .catch((e) => setError(errorMessage(e)));
  }, []);

  return (
    <div className="list-page av-page">
      <div className="list-toolbar">
        <h1>App versions</h1>
        {overview && <PolicyChips policy={overview.policy} />}
      </div>
      {overview && (
        <p className="note">
          Weekly active users are counted from backend sightings since{" "}
          {shortDay(overview.windowStart.slice(0, 10))} UTC ({overview.windowDays} days), each user
          once on their latest build.
        </p>
      )}

      {error ? (
        <div className="state-box error">App versions unavailable ({error}).</div>
      ) : !overview ? (
        <div className="state-box">Loading app versions…</div>
      ) : overview.totalWau === 0 ? (
        <div className="state-box">
          No app sightings in the last {overview.windowDays} days yet. They start as the app calls
          the backend.
        </div>
      ) : (
        <div className="av-cards">
          {overview.platforms.map((p) => (
            <PlatformCard key={p.platform} data={p} />
          ))}
          <LegacyCard overview={overview} />
        </div>
      )}

      <AdoptionSection />
    </div>
  );
}
